<script lang="ts" setup>
	import moment from 'moment'
	import { computed } from 'vue'

	import { useCurrencyFormat } from '@/composables/useCurrencyFormat'

	import { NotIncludedCharge, Rate, Total } from '../interfaces/hotelRate'
	import HotelRateDates from './ui/HotelRateDates.vue'
	import HotelRateNightlyBreakdown from './ui/HotelRateNightlyBreakdown.vue'
	import HotelRateTaxes from './ui/HotelRateTaxes.vue'
	import HotelRateTotal from './ui/HotelRateTotal.vue'
	import SectionHeader from './ui/SectionHeader.vue'

	interface PolicyInformation {
		errors?: Array<unknown>
	}

	interface Props {
		provider?: 'hotelbeds' | 'amadeus'
		startDate: string
		endDate: string
		rates: Rate[]
		total: Total
		/** Cargos que el huésped paga EN EL HOTEL (tasa turística, resort fee). */
		notIncludedCharges?: NotIncludedCharge[]
		changeCurrency: boolean
		policyInformation?: PolicyInformation | null
		canSubmit?: boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		canSubmit: true,
	})

	const nights = computed(() =>
		moment(props.endDate).diff(moment(props.startDate), 'days')
	)

	const subTotal = computed(() => {
		const totalAmount = props.rates.reduce((acc, rate) => {
			return (
				acc +
				(props.changeCurrency ? rate.dailyFare : rate.approximateDailyFare)
			)
		}, 0)
		return useCurrencyFormat(totalAmount)
	})

	const serviceChargeLabel = computed(() => {
		const detail = props.total.serviceChargeDetails
		if (detail && detail.type === 'monto_fijo') {
			const amount = detail.approximateAmount ?? detail.amount ?? '0'
			const currency = detail.requestedCurrency ?? detail.currency ?? ''
			return `${useCurrencyFormat(amount)} ${currency}`
		}
		return `${detail?.percentage ?? '0'}%`
	})

	const isButtonDisabled = computed(
		() => !!props.policyInformation?.errors?.length || !props.canSubmit
	)

	// Tarifa en otra moneda (p.ej. Hotelbeds en EUR) cobrada en MXN → mostramos el tipo
	// de cambio aplicado para que el cliente entienda de dónde sale el monto en MXN.
	const exchangeNote = computed(() => {
		const rate = props.total.exchangeRate
		const from = props.total.currencyCode
		const to = props.total.requestedCurrency
		if (!rate || !from || !to || from === to) return null
		return `Tipo de cambio aplicado: 1 ${from} = ${useCurrencyFormat(rate)} ${to}. Se cobra en ${to}.`
	})
</script>

<template>
	<div
		class="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
	>
		<!-- Header -->
		<SectionHeader
			title="Desglose de costos"
			subtitle="(por habitación)"
			icon="receipt_long"
			icon-bg-color="green"
			icon-color="green"
		/>

		<!-- Dates Section -->
		<HotelRateDates
			:start-date="startDate"
			:end-date="endDate"
		/>

		<!-- Rates Section -->
		<HotelRateNightlyBreakdown
			:rates="rates"
			:change-currency="changeCurrency"
		/>

		<!-- Taxes Section -->
		<HotelRateTaxes
			:included-in-fare="provider === 'hotelbeds'"
			:sub-total="subTotal"
			:nights="nights"
			:total-taxes="total.approximateTotalTaxes"
			:total-taxes-percentage="total.totalTaxesPercentage"
			:currency-code="total.currencyCode"
			:requested-currency="total.requestedCurrency"
			:change-currency="changeCurrency"
			:service-charge-label="serviceChargeLabel"
			:service-charge-details="total.serviceChargeDetails"
		/>

		<!-- Total -->
		<HotelRateTotal
			:fare="total.fare"
			:approximate-fare="total.approximateFare"
			:currency-code="total.currencyCode"
			:requested-currency="total.requestedCurrency"
			:change-currency="changeCurrency"
		/>

		<!-- Cargos que NO cobramos nosotros y el hotel SÍ le cobrará al huésped: tasa
		turística, resort fee. Se avisan aquí, antes de reservar, para que nadie
		reciba un cargo sorpresa en el mostrador. -->
		<div
			v-if="notIncludedCharges?.length"
			class="mx-4 mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 lg:mx-6"
		>
			<p
				class="flex items-center gap-1 text-xs font-semibold text-amber-900 font-inter"
			>
				<span class="material-symbols-outlined text-[15px]">info</span>
				A pagar directamente en el hotel
			</p>
			<ul class="mt-1 space-y-0.5">
				<li
					v-for="(charge, i) in notIncludedCharges"
					:key="i"
					class="flex justify-between gap-2 text-xs text-amber-900 font-inter"
				>
					<span>{{ charge.label }}</span>
					<span class="font-semibold whitespace-nowrap">
						{{ useCurrencyFormat(charge.amount) }} {{ charge.currency }}
					</span>
				</li>
			</ul>
			<p class="mt-1 text-[11px] text-amber-800 font-inter">
				No está incluido en el total de arriba y no se cobra ahora: lo paga el
				huésped al hotel.
			</p>
		</div>

		<!-- Tipo de cambio aplicado (tarifa en otra moneda cobrada en MXN) -->
		<p
			v-if="exchangeNote"
			class="px-4 lg:px-6 pb-3 text-xs text-gray-500 font-inter"
		>
			{{ exchangeNote }}
		</p>

		<!-- Slot for terms and conditions -->
		<slot />

		<!-- Reserve Button Desktop -->
		<div class="hidden lg:block p-4 lg:p-6 border-t border-gray-200">
			<button
				type="submit"
				form="reservation-form"
				:disabled="isButtonDisabled"
				:class="[
					'w-full px-6 py-3 lg:py-4 rounded-xl font-inter font-semibold text-white transition-all duration-300 transform text-sm lg:text-base',
					isButtonDisabled ?
						'bg-gray-400 cursor-not-allowed opacity-60'
					:	'bg-[#021F58] hover:bg-[#1C3F7A] hover:scale-105 shadow-lg hover:shadow-xl',
				]"
			>
				Reservar ahora
			</button>
		</div>
	</div>
</template>
